import React, { useState } from 'react';
import { 
  FiLayout, FiGrid, FiBarChart, FiFileText, FiSettings, 
  FiPlay, FiSave, FiEye, FiRefreshCw 
} from 'react-icons/fi';
import TemplateBuilder from '../components/templates/TemplateBuilder';
import CustomListView from '../components/listview/CustomListView';
import ReportBuilder from '../components/reports/ReportBuilder';
import QuotationBuilder from '../modules/sales/components/QuotationBuilder';
import { PersistenceTest } from '../components/debug/PersistenceTest';
import { MobileResponsiveExample } from '../components/debug/MobileResponsiveExample';

interface Tab {
  id: string;
  label: string;
  icon: any;
  component: React.ComponentType;
}

const tabs: Tab[] = [
  {
    id: 'mobile',
    label: 'Mobile Responsive Demo',
    icon: FiRefreshCw,
    component: MobileResponsiveExample
  },
  {
    id: 'persistence',
    label: 'Data Persistence Test',
    icon: FiRefreshCw,
    component: PersistenceTest
  },
  {
    id: 'templates',
    label: 'Template Builder',
    icon: FiFileText,
    component: TemplateBuilder
  },
  {
    id: 'listviews',
    label: 'Custom List Views',
    icon: FiGrid,
    component: () => <ListViewDemo />
  },
  {
    id: 'reports',
    label: 'Report Builder',
    icon: FiBarChart,
    component: ReportBuilder
  },
  {
    id: 'quotations',
    label: 'Quotation Builder',
    icon: FiLayout,
    component: QuotationBuilder
  }
];

// Sample data for list view demo
const sampleListData = [
  {
    id: '1',
    customerName: 'John Smith',
    company: 'Smith Industries',
    email: 'john@smith.com',
    phone: '+1-555-0123',
    status: 'Active',
    totalOrders: 15,
    totalValue: 45000,
    lastOrder: '2024-01-15',
    joinDate: '2023-06-01',
    region: 'North America',
    type: 'Enterprise'
  },
  {
    id: '2',
    customerName: 'Sarah Johnson',
    company: 'TechCorp Ltd',
    email: 'sarah@techcorp.com',
    phone: '+1-555-0456',
    status: 'Active',
    totalOrders: 8,
    totalValue: 22000,
    lastOrder: '2024-01-10',
    joinDate: '2023-09-15',
    region: 'North America',
    type: 'SMB'
  },
  {
    id: '3',
    customerName: 'Michael Chen',
    company: 'Global Solutions',
    email: 'michael@global.com',
    phone: '+1-555-0789',
    status: 'Inactive',
    totalOrders: 25,
    totalValue: 78000,
    lastOrder: '2023-12-20',
    joinDate: '2023-03-10',
    region: 'Asia Pacific',
    type: 'Enterprise'
  },
  {
    id: '4',
    customerName: 'Emily Davis',
    company: 'StartupX',
    email: 'emily@startupx.com',
    phone: '+1-555-0321',
    status: 'Active',
    totalOrders: 3,
    totalValue: 8500,
    lastOrder: '2024-01-08',
    joinDate: '2023-11-20',
    region: 'North America',
    type: 'Startup'
  },
  {
    id: '5',
    customerName: 'David Wilson',
    company: 'Wilson & Associates',
    email: 'david@wilson.com',
    phone: '+1-555-0654',
    status: 'Pending',
    totalOrders: 0,
    totalValue: 0,
    lastOrder: null,
    joinDate: '2024-01-01',
    region: 'Europe',
    type: 'SMB'
  }
];

const ListViewDemo: React.FC = () => {
  const [listData] = useState(sampleListData);
  
  const handleSaveView = (config: any) => {
    console.log('Saving list view config:', config);
    alert('List view configuration saved!');
  };

  const handleLoadView = (viewId: string) => {
    console.log('Loading view:', viewId);
  };

  return (
    <div className="h-full">
      <CustomListView
        data={listData}
        module="customers"
        configurable={true}
        onSaveView={handleSaveView}
        onLoadView={handleLoadView}
      />
    </div>
  );
};

export const TestCustomization: React.FC = () => {
  const [activeTab, setActiveTab] = useState('mobile');
  const [testResults, setTestResults] = useState<{ [key: string]: boolean }>({});

  const runTest = async (feature: string) => {
    console.log(`Testing ${feature}...`);
    setTestResults(prev => ({ ...prev, [feature]: true }));
    
    // Simulate test
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    alert(`✅ ${feature} test completed successfully!`);
  };

  const activeTabConfig = tabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabConfig?.component;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🧪 ERP Customization Test Suite</h1>
            <p className="text-gray-600">
              Test all customization features: Templates, List Views, Reports, and Document Builders
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-600">Test Status:</span>
              {Object.keys(testResults).length > 0 ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-600 rounded-full mr-1"></div>
                  {Object.keys(testResults).length} Tests Passed
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                  Ready to Test
                </span>
              )}
            </div>
            
            <button
              onClick={() => runTest(activeTab)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center text-sm"
            >
              <FiPlay className="w-4 h-4 mr-2" />
              Run Test
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <nav className="flex space-x-8">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const hasTestResult = testResults[tab.id];
              
              return (
                <button
                  key={tab.id}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                  {hasTestResult && (
                    <div className="w-2 h-2 bg-green-500 rounded-full ml-2"></div>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Feature Info Bar */}
      <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm">
              <span className="font-medium text-blue-900">Testing: </span>
              <span className="text-blue-800">{activeTabConfig?.label}</span>
            </div>
            
            {/* Feature capabilities for current tab */}
            <div className="flex items-center space-x-4 text-xs text-blue-700">
              {activeTab === 'templates' && (
                <>
                  <span>✓ Drag & Drop Designer</span>
                  <span>✓ Custom Fields</span>
                  <span>✓ PDF Export</span>
                  <span>✓ Template Library</span>
                </>
              )}
              {activeTab === 'listviews' && (
                <>
                  <span>✓ Column Customization</span>
                  <span>✓ Advanced Filtering</span>
                  <span>✓ Multiple View Types</span>
                  <span>✓ Export Options</span>
                </>
              )}
              {activeTab === 'reports' && (
                <>
                  <span>✓ Visual Query Builder</span>
                  <span>✓ Chart Generation</span>
                  <span>✓ Real-time Data</span>
                  <span>✓ Report Scheduling</span>
                </>
              )}
              {activeTab === 'quotations' && (
                <>
                  <span>✓ Template Selection</span>
                  <span>✓ Item Management</span>
                  <span>✓ Auto Calculations</span>
                  <span>✓ PDF Generation</span>
                </>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2 text-sm">
            <FiSettings className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800">Customization Engine Active</span>
          </div>
        </div>
      </div>

      {/* Test Content */}
      <div className="flex-1 overflow-hidden">
        {ActiveComponent && <ActiveComponent />}
      </div>

      {/* Footer - Test Results */}
      <div className="bg-white border-t border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-700">
                <strong>{Object.keys(testResults).length}</strong> features tested
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-gray-700">
                <strong>{tabs.length}</strong> total features
              </span>
            </div>
            <div className="text-gray-500">
              Coverage: <strong>{((Object.keys(testResults).length / tabs.length) * 100).toFixed(0)}%</strong>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              🎯 <strong>Goal:</strong> Test all ERP customization capabilities
            </div>
            
            {Object.keys(testResults).length === tabs.length && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                <span className="font-medium">All Tests Complete! 🎉</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feature Showcase Overlay */}
      <div className="fixed bottom-6 right-6 z-10">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">🚀 Features Showcase</h4>
            <FiEye className="w-4 h-4 text-gray-400" />
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Template Builder:</span>
              <span className={`font-medium ${testResults.templates ? 'text-green-600' : 'text-gray-400'}`}>
                {testResults.templates ? '✓ Active' : '○ Ready'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">List Views:</span>
              <span className={`font-medium ${testResults.listviews ? 'text-green-600' : 'text-gray-400'}`}>
                {testResults.listviews ? '✓ Active' : '○ Ready'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Report Builder:</span>
              <span className={`font-medium ${testResults.reports ? 'text-green-600' : 'text-gray-400'}`}>
                {testResults.reports ? '✓ Active' : '○ Ready'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Document Builder:</span>
              <span className={`font-medium ${testResults.quotations ? 'text-green-600' : 'text-gray-400'}`}>
                {testResults.quotations ? '✓ Active' : '○ Ready'}
              </span>
            </div>
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <button
              onClick={() => {
                // Run all tests
                Promise.all(tabs.map(tab => runTest(tab.id)));
              }}
              className="w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 
                       hover:from-blue-700 hover:to-purple-700 text-white rounded-md 
                       text-sm font-medium flex items-center justify-center"
            >
              <FiRefreshCw className="w-4 h-4 mr-2" />
              Test All Features
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCustomization; 